// src/pages/IndoorMapping/index.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  Chip,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Map as MapIcon,
  Upload as UploadIcon,
  GpsFixed as GeoRefIcon,
  AccountTree as GraphIcon,
  Place as POIIcon,
  PhotoCamera as PhotoIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  DeleteForever as DeleteForeverIcon,
  Layers as LayersIcon,
  Route as RouteIcon,
  DoorFront as EntranceIcon,
  Navigation as NavigationIcon,
  ThreeDRotation as ThreeDIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Admin components
import FloorPlanUploader from '../../components/maps/admin/FloorPlanUploader';
import GeoReferenceEditor from '../../components/maps/admin/GeoReferenceEditor';
import WayfindingGraphEditor from '../../components/maps/admin/WayfindingGraphEditor';
import POIEditor from '../../components/maps/admin/POIEditor';
import StepPhotoManager from '../../components/maps/admin/StepPhotoManager';

// Services
import indoorMappingService from '../../services/indoorMappingService';
import { locationsApi } from '../../services/apiClient';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`indoor-mapping-tabpanel-${index}`}
      aria-labelledby={`indoor-mapping-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const IndoorMapping = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [floorPlans, setFloorPlans] = useState([]);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Load locations on mount
  useEffect(() => {
    loadLocations();
  }, []);

  // Load floor plans when location changes
  useEffect(() => {
    if (selectedLocationId) {
      loadFloorPlans(selectedLocationId);
    } else {
      setFloorPlans([]);
      setSelectedFloorPlan(null);
    }
  }, [selectedLocationId]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await locationsApi.getAll();
      if (response.data.success) {
        // Filter to only show locations of type "Floor" (locationType = 3)
        const allLocations = response.data.data || [];
        const floorLocations = allLocations.filter(loc =>
          loc.locationType === 1 || loc.location_type === 1
        );
        setLocations(floorLocations);
      }
    } catch (err) {
      console.error('Failed to load locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const loadFloorPlans = async (locationId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await indoorMappingService.getFloorPlans(locationId);
      setFloorPlans(result || []);
      if (result?.length > 0) {
        setSelectedFloorPlan(result[0]);
      } else {
        setSelectedFloorPlan(null);
      }
    } catch (err) {
      console.error('Failed to load floor plans:', err);
      setError(err.message || 'Failed to load floor plans');
      setFloorPlans([]);
      setSelectedFloorPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleUploadComplete = (result) => {
    // Reload floor plans after upload
    if (selectedLocationId) {
      loadFloorPlans(selectedLocationId);
    }
    // Move to geo-reference tab
    setActiveTab(1);
  };

  const handleFloorPlanSelect = async (floorPlanId) => {
    try {
      const floorPlan = await indoorMappingService.getFloorPlan(floorPlanId);
      console.log('[IndoorMapping] Loaded floor plan:', floorPlan);
      setSelectedFloorPlan(floorPlan);
    } catch (err) {
      console.error('Failed to load floor plan:', err);
    }
  };

  // Refresh the currently selected floor plan (after save operations)
  const refreshSelectedFloorPlan = async () => {
    if (selectedFloorPlan?.id) {
      try {
        const floorPlan = await indoorMappingService.getFloorPlan(selectedFloorPlan.id);
        console.log('[IndoorMapping] Refreshed floor plan:', floorPlan);
        setSelectedFloorPlan(floorPlan);
        // Also refresh the list to update status indicators
        loadFloorPlans(selectedLocationId);
      } catch (err) {
        console.error('Failed to refresh floor plan:', err);
      }
    }
  };

  const getFloorPlanStatus = (floorPlan) => {
    const status = {
      hasGeoRef: floorPlan?.is_geo_referenced || false,
      hasNodes: (floorPlan?.wayfindingNodes?.length || 0) > 0,
      hasPOIs: (floorPlan?.pointsOfInterest?.length || 0) > 0,
    };
    return status;
  };

  // Get selected location name for confirmation
  const selectedLocation = locations.find((loc) => loc.id === selectedLocationId);
  const selectedLocationName = selectedLocation?.friendlyName || selectedLocation?.name || `Location ${selectedLocationId}`;

  // Handle reset location
  const handleResetLocation = async () => {
    if (!selectedLocationId || resetConfirmText !== 'RESET') return;

    setResetLoading(true);
    setError(null);

    try {
      const result = await indoorMappingService.resetLocationIndoorMapping(selectedLocationId);
      setResetResult(result.deletedCounts || result);
      // Clear local state
      setFloorPlans([]);
      setSelectedFloorPlan(null);
      // Keep dialog open to show results
    } catch (err) {
      console.error('Failed to reset location:', err);
      setError(err.message || 'Failed to reset location indoor mapping data');
      setResetDialogOpen(false);
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setResetConfirmText('');
    setResetResult(null);
  };

  const tabs = [
    { label: 'Upload', icon: <UploadIcon />, description: 'Upload PDF floor plans' },
    { label: 'Geo-Reference', icon: <GeoRefIcon />, description: 'Align to real-world coordinates' },
    { label: 'Wayfinding Graph', icon: <GraphIcon />, description: 'Create navigation paths' },
    { label: 'Points of Interest', icon: <POIIcon />, description: 'Mark important locations' },
    { label: 'Step Photos', icon: <PhotoIcon />, description: 'Add navigation photos' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
          <Link color="inherit" href="/dashboard" underline="hover">
            Dashboard
          </Link>
          <Typography color="text.primary">Indoor Mapping</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MapIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Indoor Mapping Administration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure floor plans, wayfinding graphs, and points of interest for indoor navigation
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Location & Floor Plan Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Select Location</InputLabel>
                <Select
                  value={selectedLocationId}
                  label="Select Location"
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Choose a location...</em>
                  </MenuItem>
                  {locations
                    .slice()
                    .sort((a, b) => (a.friendlyName || a.name || '').localeCompare(b.friendlyName || b.name || ''))
                    .map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.friendlyName || loc.name || `Location ${loc.id}`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!selectedLocationId || floorPlans.length === 0}>
                <InputLabel>Select Floor Plan</InputLabel>
                <Select
                  value={selectedFloorPlan?.id || ''}
                  label="Select Floor Plan"
                  onChange={(e) => handleFloorPlanSelect(e.target.value)}
                >
                  {floorPlans.map((fp) => (
                    <MenuItem key={fp.id} value={fp.id}>
                      {fp.floor_name} (Floor {fp.floor_number})
                      {fp.is_geo_referenced && (
                        <CheckCircleIcon sx={{ ml: 1, fontSize: 16, color: 'success.main' }} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              {selectedFloorPlan && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<GeoRefIcon />}
                    label={selectedFloorPlan.is_geo_referenced ? 'Geo-ref' : 'No geo-ref'}
                    color={selectedFloorPlan.is_geo_referenced ? 'success' : 'warning'}
                    size="small"
                  />
                  <Chip
                    icon={<GraphIcon />}
                    label={`${selectedFloorPlan.wayfindingNodes?.length || 0} nodes`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}
            </Grid>

            {/* Preview & Reset Buttons */}
            <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {selectedLocationId && floorPlans.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<ThreeDIcon />}
                  onClick={() => navigate('/indoor-navigation-preview')}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  3D Preview
                </Button>
              )}
              {selectedLocationId && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => setResetDialogOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Reset
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No Location Selected */}
      {!selectedLocationId && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <MapIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Select a Location to Get Started
          </Typography>
          <Typography color="text.secondary">
            Choose a location from the dropdown above to manage its indoor mapping configuration.
          </Typography>
        </Paper>
      )}

      {/* Main Content - Tabs */}
      {selectedLocationId && !loading && (
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="indoor mapping tabs"
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  label={tab.label}
                  iconPosition="start"
                  sx={{ minHeight: 64 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <CardContent>
            {/* Tab 0: Upload Floor Plans */}
            <TabPanel value={activeTab} index={0}>
              <Typography variant="h6" gutterBottom>
                Upload Floor Plans
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Upload a PDF containing floor plans. Each page will be converted to a separate floor plan image.
              </Typography>
              <FloorPlanUploader
                locationId={selectedLocationId}
                onUploadComplete={handleUploadComplete}
              />
            </TabPanel>

            {/* Tab 1: Geo-Reference */}
            <TabPanel value={activeTab} index={1}>
              {selectedFloorPlan ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Geo-Reference: {selectedFloorPlan.floor_name}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Align the floor plan to real-world coordinates by matching corners to the satellite map.
                  </Typography>
                  <GeoReferenceEditor
                    key={`geo-${selectedFloorPlan.id}-${selectedFloorPlan.is_geo_referenced}-${selectedFloorPlan.bounds_north}`}
                    floorPlan={selectedFloorPlan}
                    onSave={refreshSelectedFloorPlan}
                  />
                </>
              ) : (
                <Alert severity="info">
                  Please upload or select a floor plan first to configure geo-referencing.
                </Alert>
              )}
            </TabPanel>

            {/* Tab 2: Wayfinding Graph */}
            <TabPanel value={activeTab} index={2}>
              {selectedFloorPlan ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Wayfinding Graph: {selectedFloorPlan.floor_name}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Create waypoints and connections that define navigable paths through the building.
                  </Typography>
                  {!selectedFloorPlan.is_geo_referenced && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This floor plan is not geo-referenced. Geo-referencing is recommended for GPS navigation support.
                    </Alert>
                  )}
                  <WayfindingGraphEditor floorPlan={selectedFloorPlan} />
                </>
              ) : (
                <Alert severity="info">
                  Please select a floor plan first to edit its wayfinding graph.
                </Alert>
              )}
            </TabPanel>

            {/* Tab 3: Points of Interest */}
            <TabPanel value={activeTab} index={3}>
              {selectedFloorPlan ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Points of Interest: {selectedFloorPlan.floor_name}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Mark important locations like rooms, facilities, emergency equipment, and entrances.
                  </Typography>
                  <POIEditor floorPlan={selectedFloorPlan} />
                </>
              ) : (
                <Alert severity="info">
                  Please select a floor plan first to manage points of interest.
                </Alert>
              )}
            </TabPanel>

            {/* Tab 4: Step Photos */}
            <TabPanel value={activeTab} index={4}>
              {selectedFloorPlan ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Step Photos: {selectedFloorPlan.floor_name}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Upload photos at key navigation points to help users identify landmarks during navigation.
                  </Typography>
                  <StepPhotoManager floorPlan={selectedFloorPlan} />
                </>
              ) : (
                <Alert severity="info">
                  Please select a floor plan first to manage step photos.
                </Alert>
              )}
            </TabPanel>
          </CardContent>
        </Card>
      )}

      {/* Quick Setup Guide */}
      {selectedLocationId && floorPlans.length === 0 && !loading && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Setup Guide
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {tabs.map((tab, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Box sx={{ color: 'primary.main', mb: 1 }}>{tab.icon}</Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Step {index + 1}: {tab.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tab.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Reset Location Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteForeverIcon />
          {resetResult ? 'Reset Complete' : 'Reset Location Indoor Mapping'}
        </DialogTitle>
        <DialogContent>
          {resetResult ? (
            // Show results after reset
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Indoor mapping data for "{selectedLocationName}" has been reset successfully.
              </Alert>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Deleted Items:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><LayersIcon color="action" /></ListItemIcon>
                  <ListItemText primary={`${resetResult.floorPlans || 0} Floor Plans`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><POIIcon color="action" /></ListItemIcon>
                  <ListItemText primary={`${resetResult.pointsOfInterest || 0} Points of Interest`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GraphIcon color="action" /></ListItemIcon>
                  <ListItemText primary={`${resetResult.wayfindingNodes || 0} Wayfinding Nodes`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><RouteIcon color="action" /></ListItemIcon>
                  <ListItemText primary={`${resetResult.wayfindingEdges || 0} Wayfinding Edges`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><EntranceIcon color="action" /></ListItemIcon>
                  <ListItemText primary={`${resetResult.buildingEntrances || 0} Building Entrances`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PhotoIcon color="action" /></ListItemIcon>
                  <ListItemText primary={`${resetResult.stepPhotos || 0} Step Photos`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><RouteIcon color="action" /></ListItemIcon>
                  <ListItemText primary={`${resetResult.navigationRoutes || 0} Cached Navigation Routes`} />
                </ListItem>
              </List>
            </Box>
          ) : (
            // Show confirmation form before reset
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action will permanently delete ALL indoor mapping data for this location. This cannot be undone.
              </Alert>

              <DialogContentText sx={{ mb: 2 }}>
                You are about to reset indoor mapping data for:
              </DialogContentText>

              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {selectedLocationName}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The following data will be deleted:
              </Typography>

              <List dense sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemIcon><LayersIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={`${floorPlans.length} Floor Plan(s)`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><POIIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="All Points of Interest" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GraphIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="All Wayfinding Nodes & Edges" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><EntranceIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="All Building Entrances" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PhotoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="All Step Photos" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><RouteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="All Cached Navigation Routes" />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                To confirm, type <strong>RESET</strong> below:
              </Typography>
              <TextField
                fullWidth
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())}
                placeholder="Type RESET to confirm"
                autoComplete="off"
                error={resetConfirmText.length > 0 && resetConfirmText !== 'RESET'}
                helperText={resetConfirmText.length > 0 && resetConfirmText !== 'RESET' ? 'Please type RESET exactly' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {resetResult ? (
            <Button onClick={handleCloseResetDialog} variant="contained">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseResetDialog} disabled={resetLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleResetLocation}
                variant="contained"
                color="error"
                disabled={resetConfirmText !== 'RESET' || resetLoading}
                startIcon={resetLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
              >
                {resetLoading ? 'Resetting...' : 'Reset Location'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IndoorMapping;
